"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ClipboardCheck, Clock, ShieldCheck, MapPin, ArrowRight } from "lucide-react";
interface PlanningPillar {
  icon: React.ComponentType<{
    className?: string;
  }>;
  badge: string;
  title: string;
  description: string;
  highlights: string[];
}
const planningPillars: PlanningPillar[] = [{
  icon: ClipboardCheck,
  badge: "Phase 1",
  title: "Thorough pre-construction preparation",
  description: "A successful renovation starts months before demolition. Defining scope, securing architectural blueprints, and finalizing selections early prevents costly mid-project changes.",
  highlights: ["Precise architectural drawings and structural assessments", "Comprehensive material selection prior to ground break", "DeKalb County and City of Brookhaven zoning compliance"]
}, {
  icon: Clock,
  badge: "Phase 2",
  title: "Realistic timeline benchmarking",
  description: "Brookhaven homes often require historic or neighborhood review alongside standard building permits. Understanding typical milestones gives you peace of mind.",
  highlights: ["Permitting & engineering review (4–8 weeks average)", "Specialty millwork and custom finish procurement lead times", "Dedicated weekly milestone reviews and schedule updates"]
}, {
  icon: ShieldCheck,
  badge: "Phase 3",
  title: "Vetted contractor selection",
  description: "Hiring a general contractor with established North Atlanta trade relationships ensures structural integrity, vetted subcontractors, and reliable warranty backing.",
  highlights: ["Current state contractor registration status", "Direct carrier general liability and workers' comp verification", "Transparent cost-plus or fixed-price proposal transparency"]
}];
export default function PlanningIntro() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="renovationplanningintro" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Header Block */}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-16 sm:mb-20 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7 flex flex-col items-start gap-4">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
              <MapPin className="size-4 text-primary" />
              <span>Brookhaven & North Atlanta Renovation Guide</span>
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground leading-[1.15]">
              How to properly plan a residential renovation in Brookhaven
            </h1>

            <p className="text-base sm:text-lg text-foreground/80 leading-relaxed max-w-2xl mt-1">
              Whether modernizing a mid-century home in Ashford Park or expanding a classic residence near Historic Brookhaven, a seamless remodel relies on early planning, realistic phasing, and experienced local craftsmanship.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-md">
                <Link to="/ContactRequestAnEstimatePage">
                  Start your consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md">
                <Link to="/OurProjectsPage">View Brookhaven gallery</Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-xl overflow-hidden shadow-lg border border-border bg-muted/40 aspect-[4/3] sm:aspect-[16/11]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_renovation_architectural_plans.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_renovation_architectural_plans.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_renovation_architectural_plans.webp-wvc-sizes" alt="Detailed architectural blueprints and material swatches for a Brookhaven home renovation" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent flex items-end p-6">
                <div className="text-secondary-foreground">
                  <p className="font-serif font-medium text-lg leading-snug">Precision from day one</p>
                  <p className="text-xs text-secondary-foreground/80 mt-1">
                    Clear architectural blueprints eliminate costly mid-build revisions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Separator className="mb-14 opacity-60" />

        {/* Core Planning Pillars */}
        <div ref={gridRef} className={`transition-all duration-700 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="max-w-2xl mb-10">
            <h2 className="font-serif text-2xl sm:text-3xl font-medium tracking-tight text-foreground">
              The three fundamentals of an on-schedule remodel
            </h2>
            <p className="text-sm sm:text-base text-foreground/80 mt-2">
              Every stress-free transformation in Brookhaven rests on these foundational standards before the first hammer swings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {planningPillars.map((pillar, i) => {
            const IconComponent = pillar.icon;
            return <Card key={pillar.title} data-index={i} className="bg-card text-card-foreground border-border rounded-xl shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <div className="size-11 rounded-lg bg-accent flex items-center justify-center text-primary">
                        <IconComponent className="size-5" />
                      </div>
                      <Badge variant="secondary" className="text-xs font-mono">
                        {pillar.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-medium font-serif leading-snug text-foreground">
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-0">
                    <p className="text-sm text-foreground/80 leading-relaxed">
                      {pillar.description}
                    </p>

                    <div className="pt-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                        Key Deliverables
                      </p>
                      <ul className="space-y-2 text-xs sm:text-sm text-foreground/90">
                        {pillar.highlights.map(item => <li key={item} className="flex items-start gap-2">
                            <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>;
          })}
          </div>

          {/* Secondary Context Callout Banner */}
          <div className="mt-12 rounded-xl bg-accent/40 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="space-y-1">
              <h3 className="text-base sm:text-lg font-medium text-foreground">
                Navigating City of Brookhaven permitting?
              </h3>
              <p className="text-xs sm:text-sm text-foreground/80 max-w-2xl">
                Brookhaven Elevation Renovations oversees every local submission, variance inquiry, and inspection protocol directly with municipal offices.
              </p>
            </div>
            <Button asChild variant="secondary" className="shrink-0 rounded-md">
              <Link to="/ContactRequestAnEstimatePage">Speak with our build team</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}